/**
 * Drip Builder — Starter Kits tab
 *
 * Pre-built 7-day cadences. Clicking "Clone to My Drips" creates a drip from
 * the starter kit (POST /marketing/drips/from-template) and opens it in the
 * Drip Builder tab. Relocated from the old Template Library page top section.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Star, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { apiPost } from '../../../lib/api.js';
import { DEFAULT_BIRTHDAY } from './BirthdayDripEditor.jsx';

// ── Starter Drip Kits (sourced from api/src/services/starterDrips.js) ────────────
// These are kept in sync with the backend starterKey names.
export const STARTER_DRIP_KITS = [
  {
    key: 'veterans',
    title: '7-Day Starter — Veterans Life',
    description: 'SGLI/VGLI gap coverage cadence for veteran leads. 14 steps (SMS + email each day).',
    vertical: 'veteran',
    steps: [
      { day: 1, type: 'SMS', preview: 'Hi #firstname, #agentname with #company — first off, thank you for your service…' },
      { day: 1, type: 'Email', preview: 'Thank you for your service — your options: VGLI vs. civilian term life comparison…' },
      { day: 2, type: 'SMS', preview: 'Are you currently on SGLI, VGLI, or no coverage right now?' },
      { day: 2, type: 'Email', preview: 'The VGLI gap most vets miss — same vet can often save 30–50%…' },
      { day: 3, type: 'SMS', preview: 'Any VA-rated disabilities? Doesn’t disqualify you—helps pick the right carrier…' },
      { day: 3, type: 'Email', preview: 'VA-rated conditions and life insurance: PTSD, sleep apnea, TBI…' },
      { day: 4, type: 'SMS', preview: 'Open at 4 PM and 6 PM today. Either work for a 10-min call?' },
      { day: 4, type: 'Email', preview: 'Real example — Marine, 41, 70% VA: saved $36/mo vs. VGLI…' },
      { day: 5, type: 'SMS', preview: 'Want me to run a real quote, or is the timing off right now?' },
      { day: 5, type: 'Email', preview: 'Why I work specifically with veterans…' },
      { day: 6, type: 'SMS', preview: 'Closing the file this week. Reply KEEP if you want me to keep going…' },
      { day: 6, type: 'Email', preview: 'Last call before I close out — DOB + branch + VA rating…' },
      { day: 7, type: 'SMS', preview: "Door's always open #firstname. Thanks again for your service." },
      { day: 7, type: 'Email', preview: "Open anytime — thank you again. File's closed for now…" },
    ],
  },
  {
    key: 'final_expense',
    title: '7-Day Starter — Final Expense',
    description: 'Plug-and-play 7-day cadence for Final Expense leads. 14 steps.',
    vertical: 'final_expense',
    steps: [
      { day: 1, type: 'SMS', preview: 'Hi #firstname, this is #agentname… I got your request about a final expense plan…' },
      { day: 1, type: 'Email', preview: 'Thanks for reaching out about a Final Expense plan. Usually $5,000–$25,000…' },
      { day: 2, type: 'SMS', preview: 'Most plans we set up are under $1/day for $10K of coverage…' },
      { day: 2, type: 'Email', preview: 'Two questions before I send your quote: DOB and tobacco use…' },
      { day: 3, type: 'SMS', preview: 'Are you looking to lock in pricing while you’re still healthy?…' },
      { day: 3, type: 'Email', preview: 'Why agents recommend locking in early…' },
      { day: 4, type: 'SMS', preview: 'I reserved 4:00 and 6:00 today. Either work? Just need 5 mins…' },
      { day: 4, type: 'Email', preview: 'A real example — Maria, age 67, $15K for $42/month…' },
      { day: 5, type: 'SMS', preview: 'Reply STOP if you’d like me to step back, or YES if you’d like a quote…' },
      { day: 5, type: 'Email', preview: 'Three things that surprise people about final expense…' },
      { day: 6, type: 'SMS', preview: 'Last text before I close out your request. Want me to hold your quote?…' },
      { day: 6, type: 'Email', preview: 'Closing your file — last call. Reply DOB and I’ll get you a number…' },
      { day: 7, type: 'SMS', preview: 'Final follow-up #firstname. Door’s always open if you change your mind…' },
      { day: 7, type: 'Email', preview: "No response so I'll step back. Number's saved in this thread…" },
    ],
  },
  {
    key: 'term_life',
    title: '7-Day Starter — Term Life',
    description: '7-day cadence for Term Life leads. Edit to taste. 14 steps.',
    vertical: 'generic',
    steps: [
      { day: 1, type: 'SMS', preview: 'Hi #firstname, #agentname with #company. Saw you requested a term life quote…' },
      { day: 1, type: 'Email', preview: 'Term life is the simplest, most affordable life insurance…' },
      { day: 2, type: 'SMS', preview: 'Are you looking to cover a mortgage, income for the family, or both?…' },
      { day: 2, type: 'Email', preview: 'Term length framework: 10/20/30-year — which one fits you?…' },
      { day: 3, type: 'SMS', preview: 'Term rates haven’t been this low in years and they go up every birthday…' },
      { day: 3, type: 'Email', preview: 'No medical exam options — up to $1M, active in 24–72 hours…' },
      { day: 4, type: 'SMS', preview: 'I have a 4 PM and 6 PM open today. Either work for a 10-min call?…' },
      { day: 4, type: 'Email', preview: 'Common mistake people make with term life — getting too little coverage…' },
      { day: 5, type: 'SMS', preview: 'Still want me to run that quote? Reply LATER and I’ll circle back…' },
      { day: 5, type: 'Email', preview: 'Term vs. permanent — which makes sense for you?…' },
      { day: 6, type: 'SMS', preview: 'Closing out your file this week. Reply KEEP/PAUSE/STOP…' },
      { day: 6, type: 'Email', preview: 'Last note before I close your file — DOB + tobacco status…' },
      { day: 7, type: 'SMS', preview: "Door's always open #firstname. If anything changes, you know where to find me." },
      { day: 7, type: 'Email', preview: "Closing your file for now. Save my number — I'll have rates ready in minutes…" },
    ],
  },
  {
    key: 'mortgage_protection',
    title: '7-Day Starter — Mortgage Protection',
    description: '7-day cadence for Mortgage Protection leads. Covers home payoff on death. 14 steps.',
    vertical: 'mortgage_protection',
    steps: [
      { day: 1, type: 'SMS', preview: 'Hi #firstname, #agentname… your home is safe whether something happens to you or not…' },
      { day: 1, type: 'Email', preview: 'Mortgage protection is term life sized to pay off your home if you pass away…' },
      { day: 2, type: 'SMS', preview: 'What’s your remaining mortgage balance roughly?…' },
      { day: 2, type: 'Email', preview: 'Mortgage protection vs. regular term — key difference explained…' },
      { day: 3, type: 'SMS', preview: 'Does anyone besides you contribute to the mortgage payment?…' },
      { day: 3, type: 'Email', preview: 'Real example — the Garcias: $58/month for $350K of 30-year level term…' },
      { day: 4, type: 'SMS', preview: 'Most people wrap this in under 15 mins, no medical exam. Want to grab 4 PM today?…' },
      { day: 4, type: 'Email', preview: 'No exam, instant decision — coverage up to $1M, 24–72 hours…' },
      { day: 5, type: 'SMS', preview: 'Your home is your biggest asset. Want me to lock in protection for it?…' },
      { day: 5, type: 'Email', preview: 'What if you have group life through work? It ends the day you leave…' },
      { day: 6, type: 'SMS', preview: 'Closing your file this week. Quick reply: KEEP, PAUSE, or STOP…' },
      { day: 6, type: 'Email', preview: 'Closing your file — reply DOB + tobacco status for a quote…' },
      { day: 7, type: 'SMS', preview: "Door's open #firstname — anytime. Take care." },
      { day: 7, type: 'Email', preview: 'File’s closed for now. When you refinance or move, send me a text…' },
    ],
  },
  {
    key: 'iul',
    title: '7-Day Starter — IUL',
    description: 'Life insurance + tax-advantaged retirement strategy cadence. 14 steps.',
    vertical: 'iul',
    steps: [
      { day: 1, type: 'SMS', preview: 'Hi #firstname, #agentname… life coverage plus a tax-advantaged retirement bucket…' },
      { day: 1, type: 'Email', preview: 'IUL = Indexed Universal Life. Cash value tracks the S&P 500 with a floor…' },
      { day: 2, type: 'SMS', preview: 'To run accurate IUL numbers I just need: DOB, tobacco, funding target…' },
      { day: 2, type: 'Email', preview: 'IUL vs. 401k — friendly comparison: tax-free distributions via policy loans…' },
      { day: 3, type: 'SMS', preview: 'Are you maxing your 401k/IRA already? IUL makes most sense as a SUPPLEMENT…' },
      { day: 3, type: 'Email', preview: 'How the cap and floor actually work — floor 0%, cap 9–12%…' },
      { day: 4, type: 'SMS', preview: 'IUL needs to be designed RIGHT or it underperforms. Mine are designed for max accumulation…' },
      { day: 4, type: 'Email', preview: 'Three IUL mistakes to avoid: over-funded death benefit, under-funded premiums…' },
      { day: 5, type: 'SMS', preview: 'IUL applications can take 30–60 days — the sooner we start, the sooner cash value grows…' },
      { day: 5, type: 'Email', preview: 'A 20-year illustration: $500/mo for 30 years → $850K–1.1M projected…' },
      { day: 6, type: 'SMS', preview: 'Closing the file this week. IUL is a 30-year strategy — every month delayed is lost…' },
      { day: 6, type: 'Email', preview: 'Last call — reply DOB + monthly funding target for an illustration…' },
      { day: 7, type: 'SMS', preview: "Door's open #firstname. IUL is a long game — when you're ready, I'm here." },
      { day: 7, type: 'Email', preview: 'Closing for now — when bonus season hits or you want to diversify retirement…' },
    ],
  },
];

export default function StarterKitsTab() {
  const navigate = useNavigate();

  const useTemplateMutation = useMutation({
    mutationFn: ({ templateId, starterKey }) =>
      apiPost('/marketing/drips/from-template', templateId ? { templateId } : { starterKey }),
    onSuccess: (resp) => {
      toast.success(`Drip "${resp?.name || 'Drip'}" created — opening builder…`);
      navigate(`/marketing-hub/drip-builder?dripGroupId=${resp.dripGroupId}&edit=true`);
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Failed to create drip from template'),
  });

  return (
    <div className="space-y-3" data-testid="starter-kits-tab">
      <div>
        <div className="text-white font-bold text-lg">Starter Drips</div>
        <div className="text-white/50 text-sm">
          Agency-tested cadences. Preview any starter, then clone it into My Custom Drips to edit it.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {STARTER_DRIP_KITS.map((kit) => (
          <StarterKitCard
            key={kit.key}
            kit={kit}
            loading={useTemplateMutation.isPending}
            onUse={() => useTemplateMutation.mutate({ starterKey: kit.key })}
          />
        ))}
        <BirthdayStarterCard />
        <SoldPersistencyCard />
      </div>
    </div>
  );
}

/** Starter: one greeting every year on the contact's birthday. Clone creates the drip and opens it. */
function BirthdayStarterCard() {
  const navigate = useNavigate();
  const clone = useMutation({
    mutationFn: () => apiPost('/marketing/drips', {
      name: 'Annual Client Birthday Greeting',
      dripType: 'birthday',
      trigger: 'manual',
      birthday: { sendTime: DEFAULT_BIRTHDAY.sendTime, body: DEFAULT_BIRTHDAY.body, attachDigitalBusinessCard: false },
    }),
    onSuccess: (resp) => {
      toast.success('Birthday drip added to My Custom Drips');
      navigate(`/marketing-hub/drip-builder?dripGroupId=${resp.drip.dripGroupId}&edit=true`);
    },
    onError: (err) => toast.error(err?.response?.status === 409
      ? 'You already have this drip — open it from My Custom Drips.'
      : (err?.response?.data?.error || 'Could not add the birthday drip')),
  });
  return (
    <div className="panel p-4 flex flex-col gap-3 border border-emerald-500/20" data-testid="birthday-starter-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-bold text-white text-sm flex items-center gap-2"><Star className="w-4 h-4 text-emerald-400" /> Annual Client Birthday Greeting</div>
          <div className="text-xs text-white/50 mt-0.5">One text every year on the contact's birthday, at 10:00 AM their time, until you remove them.</div>
        </div>
        <span className="text-[10px] uppercase tracking-wide bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-sm shrink-0">Annual</span>
      </div>
      <div className="rounded-md bg-white/5 px-3 py-2 text-xs text-white/60">{DEFAULT_BIRTHDAY.body}</div>
      <div className="flex gap-2 mt-auto pt-2 border-t border-white/5">
        <button onClick={() => clone.mutate()} disabled={clone.isPending} data-testid="birthday-starter-clone" className="btn-primary flex-1 text-sm flex items-center justify-center gap-1 py-2">
          {clone.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />} Clone to My Drips
        </button>
      </div>
    </div>
  );
}

/** Starter: the 12-month retention texts. Preview them, or clone into My Custom Drips to edit them. */
function SoldPersistencyCard() {
  const navigate = useNavigate();
  const clone = useMutation({
    mutationFn: () => apiPost('/marketing/drips', { name: '12-Month Sold Retention', dripType: 'sold_retention', trigger: 'manual', retention: { sendTime: '10:00', milestones: [] } }),
    onSuccess: (resp) => {
      toast.success('Retention drip added to My Custom Drips');
      navigate(`/marketing-hub/drip-builder?dripGroupId=${resp.drip.dripGroupId}&edit=true`);
    },
    onError: (err) => toast.error(err?.response?.status === 409
      ? 'You already have this drip — open it from My Custom Drips.'
      : (err?.response?.data?.error || 'Could not add the retention drip')),
  });
  return (
    <div className="panel p-4 flex flex-col gap-3 border border-civix-purple/30" data-testid="sold-persistency-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-bold text-white text-sm flex items-center gap-2">
            <Star className="w-4 h-4 text-civix-purpleSoft" /> 12-Month Sold Retention
          </div>
          <div className="text-xs text-white/50 mt-0.5">Runs by itself when a lead is marked Sold and you enter the first draft date. Clone it to edit the texts and timing.</div>
        </div>
        <span className="text-[10px] uppercase tracking-wide bg-civix-purple/20 text-civix-purpleSoft px-2 py-0.5 rounded-sm shrink-0">From draft date</span>
      </div>
      <ul className="space-y-1 text-xs text-white/60">
        {[['Draft − 48 hours', 'Bank-draft reminder'], ['Draft + 14 days', 'Policy packet check-in'], ['Draft + 90 days', '3-month persistency touch'], ['Draft + 5 months', 'Half-year review'], ['Draft + 11 months', 'Anniversary review']].map(([when, what]) => (
          <li key={when} className="flex gap-2"><span className="shrink-0 px-1.5 py-0.5 rounded-sm bg-civix-panelLight font-mono">{when}</span><span>{what}</span></li>
        ))}
      </ul>
      <div className="flex gap-2 mt-auto pt-2 border-t border-white/5">
        <button onClick={() => clone.mutate()} disabled={clone.isPending} data-testid="retention-starter-clone" className="btn-primary flex-1 text-sm flex items-center justify-center gap-1 py-2">
          {clone.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />} Clone to My Drips
        </button>
      </div>
    </div>
  );
}

// ── Starter Drip Kit Card ─────────────────────────────────────────────────────
function StarterKitCard({ kit, onUse, loading }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="panel p-4 flex flex-col gap-3 border border-emerald-500/20">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-bold text-white text-sm flex items-center gap-2">
            <Star className="w-4 h-4 text-emerald-400" />
            {kit.title}
          </div>
          <div className="text-xs text-white/50 mt-0.5">{kit.description}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wide bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-sm shrink-0">
          Starter
        </span>
      </div>

      {/* Step preview list */}
      <div className="space-y-1">
        {(expanded ? kit.steps : kit.steps.slice(0, 4)).map((s, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-white/60">
            <span className="shrink-0 px-1.5 py-0.5 rounded-sm bg-civix-panelLight font-mono">
              D{s.day} {s.type}
            </span>
            <span className="truncate">{s.preview}</span>
          </div>
        ))}
        {kit.steps.length > 4 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-white/40 hover:text-white/70 flex items-center gap-1 mt-1"
          >
            {expanded
              ? <><ChevronUp className="w-3 h-3" /> Hide steps</>
              : <><ChevronDown className="w-3 h-3" /> +{kit.steps.length - 4} more steps</>}
          </button>
        )}
      </div>

      <div className="flex gap-2 mt-auto pt-2 border-t border-white/5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`starter-kit-preview-${kit.key}`}
          className="btn-ghost text-sm py-2 px-3"
        >
          {expanded ? 'Hide' : 'Preview'}
        </button>
        <button
          onClick={onUse}
          disabled={loading}
          data-testid={`starter-kit-use-${kit.key}`}
          className="btn-primary flex-1 text-sm flex items-center justify-center gap-1 py-2"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
          Clone to My Drips
        </button>
      </div>
    </div>
  );
}
